import { showPopup } from './../initHelper/initPopups';
import fetchIntercept from 'fetch-intercept';
import { sleep } from '../utils/sleep';
import { enFetch } from '../utils/fetchUtils';

/*
* Background work is disabled because of too many opened tabs. Token is updated inside interceptor "fetchIntercept.register"
*
* When request comes to the same domain (EN.Gine), server redirects it to EN.Auth. Interceptor makes next steps:
* 1. Save request in memory.
* 2. Try to get info from /TokenInfo endpoint
*   a. Token is valid - update in localstorage
*   b. Token is outdated - open iFrame to get new token
* 3. Check whether token is updated
* 4. Send saved request with new token again
* 
* When request comes to EN.Api, second step is replaced by:
* request to EN.Auth server with refresh token
* 
* For local test:
* 1. Reduce token lifetime to 70 seconds in Database (Table Clients row with client EndataUi column named AccessTokenLifetime).
* 2. Change EndataApi/ServiceRegistration for (ValidateLifetime = true).
* 3. Start application.
* 4. log in as usual.
* 5. Open any page with API calls (e.g. Vorgänge).
* 6. Wait for 6 minutes (70 seconds for token lifetime and 5 minutes for disable lifetimevalidation).
* 7. Apply some filter or load other 10 rows.
*   in Console there will be 401 exception, token request and again search
* 8. Open different page, which uses data, but not from API (e.g. Bestandsdaten)
* 9. Wait for 70 seconds.
* 10. Apply some filter or load other 10 rows.
*   in Console there will be 302 redirect as response for search, token info and maybe Sitzung abgelaufen message. After successful close data will be loaded automatically.
* 
* */
export default function tokenRefresher() {
    let authServerUrl = null;
    let engineClientId = null;
    let iFrame = document.createElement('iframe');
    let initialLoading = true;
    let requests = [];

    function addIframeToBody() {
        iFrame = document.createElement('iframe');
        $(iFrame).css('display', 'none');
        document.body.append(iFrame);
    }

    /**
     * Global function. Returns header for requests to EN.Api
     * @param {String} potentialToken if token is integrated through Razor, use it as plan B.
     */
    window.getTokenForHeader = function (potentialToken) {
        return {Authorization: 'Bearer ' + (localStorage.getItem('accessToken') ?? potentialToken)};
    };

    /**
     * Start refresh token procedure.
     * successful response described https://worxapp.atlassian.net/wiki/spaces/07PORT/pages/2432204801/Mit+der+API+arbeiten#Successful-grant
     * works with multiple tabs with using localstorage
     */
    async function updateToken() {
        window.removeEventListener('online', updateToken);
        const refreshToken = localStorage.getItem('refreshToken');
        const formData = new FormData();
        formData.append('client_id', engineClientId);
        formData.append('grant_type', 'refresh_token');
        formData.append('refresh_token', refreshToken);
        try {
            const resp = await fetch(authServerUrl, {method: 'POST', body: formData});
            if (!resp.ok) {
                return false;
            }
            const data = await resp.json();
            localStorage.setItem('accessToken', data.access_token);
            localStorage.setItem('refreshToken', data.refresh_token);
            return true;

        } catch (error) {
            if (!navigator.onLine) {
                //If internet connection is off, subscribe for reconnect
                window.addEventListener('online', updateToken);
            }
        }
        return false;
    }

    /**
     * If token expired and Auth is logged off, open popup for user to enter credentials
     * @param {String} url - url to Auth server login page.
     */
    function openLoginPage(url) {
        //open window in prompt
        const newWindow = window.open(url, '_blank', 'width=600,height=700');
        let attempt = 0;
        let interval = setInterval(async () => {
            attempt++;
            //after minute restart
            //every half second check, whether Auth window is closed. If closed, then user entered credentials and we can try to update token info 
            if (attempt > 120 || newWindow.closed) {
                clearInterval(interval);
                iFrame.src = location.origin;
                await sleep(1000);
                await getToken();
                // //If user came from main page, token is not updated. Force reload helps with that
                if (initialLoading) {
                    location.reload();
                }
                initialLoading = false;
            }
        }, 500);
    }

    /**
     * Initial token receiving.
     */
    async function getToken() {
        const resp = await fetch('/api/security/TokenInfo', {method: 'POST'});
        if (resp.ok) {
            const tokenInfo = await resp.json();

            if (tokenInfo.ReturnUrl) {
                showPopup('Sitzung abgelaufen', 'Ihre EN.Gine Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.', 'Anmelden', () => openLoginPage(tokenInfo.ReturnUrl));
            } else {
                localStorage.setItem('accessToken', tokenInfo.Token);
                localStorage.setItem('refreshToken', tokenInfo.RefreshToken);
                //To check whether token already updated or not
                localStorage.setItem('tokenUpdatedAt', new Date());
            }
        }
    }

    /**
     * Global function to initiate procedure of token receiving
     * @param {String} authUrl - url to Auth server connect endpoint.
     * @param {String} clientId - id of Api client.
     */
    window.startRefreshLoginCheck = function (authUrl, clientId) {

        if (window === window.parent) {
            addIframeToBody();
            //After page load, receive information about access_token lifetime
            getToken();
        }
        authServerUrl = authUrl;
        engineClientId = clientId;
        // Register interceptor for update Cookies
        const unregister = fetchIntercept.register({
            request: async function (url, config) {
                if (!url.includes('TokenInfo') && !url.includes('notificationsHub')) {
                    requests.push({url, config});
                }
                return [url, config];
            },
            response: async function (response) {
                const redirectedUrl = new URL(response.url);
                const authUrl = new URL(authServerUrl);
                const currentTimeStamp = new Date();
                if(requests.length > 0) {
                    initialLoading = false;                    
                } 
                //If token is outdated, server will try to redirect request to Auth server. In that case type will be cors (different domain) 
                if ((response.type === 'cors' && response.redirected && authUrl.host === redirectedUrl.host)) {
                    iFrame.src = location.origin;
                    let attempts = 0;
                    while (true) {
                        const localStorageTimeStamp = new Date(localStorage.getItem('tokenUpdatedAt'));
                        if (attempts++ > 1) {
                            attempts = 0;
                            $(iFrame).remove();
                            addIframeToBody();
                            iFrame.src = location.origin;
                            await sleep(1000);
                            await getToken();
                            continue;
                        }
                        //Wait 1 second and check whether token is updated
                        await sleep(1000);
                        if (currentTimeStamp < localStorageTimeStamp) {
                            $(iFrame).remove();
                            break;
                        }
                    }
                    // Send request again
                    try {
                        const config = {
                            body: response.request.body,
                            headers: response.request.headers,
                            method: response.request.method
                        };
                        config.headers.set('Authorization', window.getTokenForHeader().Authorization);
                        return await fetch(response.request.url, config);
                    } catch {
                        // This will work only when something intercepted request and body stream is locked. Plan B for this case
                        // For example, EN.Send resend same request leads to exception: 
                        // TypeError: Failed to construct 'Request': The provided ReadableStream is disturbed
                        let foundRequest = requests.find(req => response.request.url.includes(req.url));
                        return await fetch(foundRequest.url, foundRequest.config);
                    }
                } else if (response.status === 401) {
                    for (let attempt = 0; attempt < 3; attempt++) {
                        const tokenUpdated = await updateToken();
                        if (tokenUpdated === true) break;
                        if (attempt === 2) {
                            return null;
                        }
                    }
                    const config = {
                        body: response.request.body,
                        headers: response.request.headers,
                        method: response.request.method
                    };
                    const bearerToken = window.getTokenForHeader(true);
                    config.headers.set('Authorization', bearerToken.Authorization);
                    return await fetch(response.request.url, config);
                } else if (response.status === 400) {
                    // Edge case when token is expired and request not to API, but to EN.Auth server, 
                    // response comes with 400 exception instead of 401
                    // In that case server adds special header so we can handle it here
                    const existingHeader = response.headers.has('token-is-expired');

                    if (existingHeader) {
                        const tokenUpdated = await updateToken();
                        // Can not send request again because of an exception 'Request body is disturbed'
                        // That means, that something in js tries to read 400 exception by itself and blocks stream
                        // In some cases it is possible to read it, but not always, and that leads to bugs

                        // So, when token successfully updated, we throw an exception to source, 
                        // and we can resend request manually
                        if (tokenUpdated) {
                            throw new Error('token-is-expired');
                        }
                    }
                }
                //If, for example, we call two requests simultaneously, both will encounter token expired exception. 
                // And both we have two manage. 
                // Because of unable usage of response.request, We use this array, where put every request. 
                // When it was succeeded, here simply remove successful one, leaving only those, that still in processing
                requests = requests.filter(req => !response.request.url.includes(req.url));
                // Modify the response object
                return response;
            }
        });
    };
}